import { InlineNotification, Tag } from '@carbon/react';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { FC, useMemo, useState } from 'react';
import { Link, generatePath, useHistory } from 'react-router-dom';
import routes from '~/routes';
import { parseUTCdateTime } from 'utils/dates';
import { formatDuration } from 'utils/duration';
import { STATUS_DISPLAY_MAP } from 'pages/crm-tracts-subtracts/utils';
import { useBatchList } from './hooks';
import type { BatchListItem, BatchStatus } from './types';

type Row = BatchListItem & Record<string, unknown>;

const PER_PAGE = 20;

const STATUS_TAG_TYPES: Record<
  BatchStatus,
  'cool-gray' | 'blue' | 'green' | 'red'
> = {
  pending: 'cool-gray',
  processing: 'blue',
  processed: 'green',
  failed: 'red',
};

const PLAIN_CONFIG = {
  sorting: false,
  globalSearch: false,
  columnFilters: false,
  columnVisibility: false,
  columnResizing: false,
  columnReordering: false,
  columnPinning: false,
  export: false,
} as const;

const count = (accessorKey: keyof BatchListItem, header: string) => ({
  accessorKey,
  header,
  size: 120,
  meta: { type: 'integer' as const },
});

/**
 * The seconds a batch took, once it has finished.
 * @param {BatchListItem} batch - A listed batch
 * @return {number | null} Seconds, or null while it runs
 */
const durationSeconds = (batch: BatchListItem) => {
  if (!batch.startedAt || !batch.completedAt) return null;
  return (Date.parse(batch.completedAt) - Date.parse(batch.startedAt)) / 1000;
};

/**
 * The Bulk uploads tab: every bulk asset batch, newest first, a page at a
 * time. A row opens that batch's results.
 * @return {JSX.Element} The list
 */
/**
 * The CRM page opens on this list when its URL carries this query. Not a hash:
 * the navigation guard lets hash links through as in-page jumps.
 */
export const BULK_UPLOADS_SEARCH = '?tab=bulk-uploads';
export const BULK_UPLOADS_LIST_PATH = `${routes.crmTractsAndSubtractsPath}${BULK_UPLOADS_SEARCH}`;

export const BulkUploadsTable: FC = () => {
  const history = useHistory();
  const [page, setPage] = useState({ page: 1, pageSize: PER_PAGE });
  const { list, error, loading } = useBatchList(page.page, page.pageSize);

  const columns = useMemo<DataTableColumnDef<Row>[]>(
    () => [
      {
        id: 'run',
        header: 'Run',
        size: 220,
        cell: ({ row }) => (
          <span>
            {/* A link too, so a keyboard user can open the batch. */}
            <Link
              to={generatePath(routes.crmBulkUploadPath, {
                id: row.original.id,
              })}
              onClick={(event) => event.stopPropagation()}
            >
              <strong>#{row.original.id}</strong>
            </Link>{' '}
            {parseUTCdateTime(
              row.original.startedAt ?? row.original.createdAt,
              true
            )}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        cell: ({ row }) => (
          <Tag type={STATUS_TAG_TYPES[row.original.status]} size="sm">
            {STATUS_DISPLAY_MAP[row.original.status]}
          </Tag>
        ),
      },
      { accessorKey: 'ranBy', header: 'Ran by', size: 200 },
      count('rowCount', 'Rows'),
      count('createdCount', 'Created'),
      count('withIssuesCount', 'With issues'),
      count('failedCount', 'Could not create'),
      count('skippedCount', 'Skipped'),
      {
        id: 'duration',
        header: 'Duration',
        size: 120,
        cell: ({ row }) => formatDuration(durationSeconds(row.original)),
      },
    ],
    []
  );

  if (error && !list) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        hideCloseButton
        title="Couldn’t load bulk uploads"
        subtitle={error}
      />
    );
  }

  if (list && list.pagination.totalCount === 0) {
    return (
      <InlineNotification
        kind="info"
        lowContrast
        hideCloseButton
        title="No bulk uploads yet"
        subtitle="Uploads started from Create CRM Asset, Bulk Upload Assets, are listed here."
      />
    );
  }

  return (
    <>
      {/* Shown over the last rows that loaded: a later page or poll failed. */}
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Couldn’t load bulk uploads"
          subtitle={error}
        />
      )}
      <DataTable<Row>
        data={(list?.batches ?? []) as Row[]}
        columns={columns}
        getRowId={(row) => String(row.id)}
        isLoading={loading}
        config={PLAIN_CONFIG}
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount: list?.pagination.totalCount ?? 0,
        }}
        initialTableState={{ pageSize: PER_PAGE }}
        pageSizes={[PER_PAGE, 50, 100]}
        onPaginationChange={(state) =>
          setPage({ page: state.page, pageSize: state.pageSize })
        }
        onSelectionChange={(state) => {
          const id = state.selection.toggledNodes[0];
          if (id) history.push(generatePath(routes.crmBulkUploadPath, { id }));
        }}
      />
    </>
  );
};
